// The screenshot library is loaded lazily at runtime rather than bundled, so
// the core widget stays tiny and screenshots never weigh down first paint.
// The URL is overridable (e.g. to self-host the chunk instead of a public CDN).
const DEFAULT_HTML_TO_IMAGE_URL = 'https://esm.sh/html-to-image@1.11.11';

interface HtmlToImage {
  toBlob: (node: HTMLElement, options?: Record<string, unknown>) => Promise<Blob | null>;
  toCanvas: (node: HTMLElement, options?: Record<string, unknown>) => Promise<HTMLCanvasElement>;
}

let modPromise: Promise<HtmlToImage> | null = null;

function loadLib(): Promise<HtmlToImage> {
  if (!modPromise) {
    const url =
      (window as unknown as { FeedbackProConfig?: { screenshotLibUrl?: string } }).FeedbackProConfig
        ?.screenshotLibUrl ?? DEFAULT_HTML_TO_IMAGE_URL;
    // Variable specifier → esbuild keeps this as a native runtime import().
    modPromise = import(/* @vite-ignore */ url) as Promise<HtmlToImage>;
  }
  return modPromise;
}

const RENDER_OPTS = {
  cacheBust: true,
  // A feedback screenshot doesn't need embedded web fonts (that step can stall
  // on external CSS).
  skipFonts: true,
  filter: (node: unknown) => !(node instanceof HTMLElement && node.id === 'feedback-pro-root'),
};

function pixelRatio(): number {
  return Math.min(window.devicePixelRatio || 1, 2);
}

export interface RegionRect {
  /** Page coordinates (viewport + scroll offset). */
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Capture a user-selected rectangular region of the page as a PNG blob. Renders
 * the page to a canvas (html-to-image) then crops to the region — no native
 * screen-share prompt. Best-effort: returns null on failure/timeout.
 */
export async function captureRegion(rect: RegionRect, timeoutMs = 10000): Promise<Blob | null> {
  try {
    const { toCanvas } = await loadLib();
    const dpr = pixelRatio();

    const render = toCanvas(document.body, { ...RENDER_OPTS, pixelRatio: dpr });
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
    const full = await Promise.race([render, timeout]);
    if (!full) return null;

    const crop = document.createElement('canvas');
    crop.width = Math.max(1, Math.round(rect.width * dpr));
    crop.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = crop.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(
      full,
      Math.round(rect.x * dpr),
      Math.round(rect.y * dpr),
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height,
    );

    return await new Promise<Blob | null>((resolve) => crop.toBlob(resolve, 'image/png'));
  } catch {
    return null;
  }
}
